import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';

import NotificationSidebar from "./NotificationSidebar";
import RightSidebar from "./Sidebar/RightSidebar";
import IASideBar from "./IASideBar";

import logoSAV from '../logoSAV.png';

function SideBar() {
    const location = useLocation();
    const { logout, isLoggedIn, user, hasRole } = useAuth();
    const { notifications, unread, refresh } = useNotifications();

    const [isRightSideBarOpen, setIsRightSideBarOpen] = useState(false);
    const [rightSideBarTab, setRightSideBarTab] = useState('notif');

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    const popupRef = useRef();
    const popupTriggerRef = useRef();

    const menuRef = useRef(null);
    const indicatorRef = useRef(null);

    const navItemsTop = [
        { to: "/dashboard", label: "Tableau de Bord", icon: "fa-solid fa-house-chimney", match: (p) => p.startsWith("/dashboard") },
        hasRole('visiteur') && { to: "/invitations", label: "Invitations", icon: "fa-solid fa-scroll", match: (p) => p.startsWith("/invitations" || "/declarations") },
        !hasRole('visiteur') && { to: "/sites", label: "Sites", icon: "fa-solid fa-city", match: (p) => p.startsWith("/sites") },
        !hasRole('visiteur') && { to: "/declarations", label: "Déclarations", icon: "fa-solid fa-scroll", match: (p) => p.startsWith("/declarations") },
        !hasRole('visiteur') && { to: "/dommages", label: "Dommages", icon: "fa-solid fa-fire-extinguisher", match: (p) => p.startsWith("/dommages") },
    ].filter(Boolean);

    const navItemsMid = [
        /*!hasRole('visiteur') && { to: "#2", label: "Provisions Intervenant", icon: "fa-solid fa-circle-notch", match: (p) => false },
        !hasRole('visiteur') && { to: "#1", label: "Expertises", icon: "fa-solid fa-comments", match: (p) => false },*/
        !hasRole('visiteur') && { to: "/intervenants", label: "Intervenants", icon: "fa-solid fa-screwdriver-wrench", match: (p) => p.startsWith("/intervenants") },
        //!hasRole('visiteur') && { to: "/personnels", label: "Personnels", icon: "fa-solid fa-user", match: (p) => p.startsWith("/personnels") },

    ].filter(Boolean);

    const navItemsBottom = !hasRole('visiteur') ? [
        { to: "/statistiques", label: "Statistiques", icon: "fa-solid fa-chart-pie", match: (p) => p.startsWith("/statistiques") },
        {
            label: "Messagerie", icon: "fa-solid fa-comment",
            action: () => {
                setIsRightSideBarOpen(true);
                setRightSideBarTab("mess");
            },
            match: () => isRightSideBarOpen && rightSideBarTab === "mess",
        },
    ] : [];

    const navItemsAgenda = !hasRole("visiteur") ? [
        {
            to: "/agenda",
            label: "Agenda",
            icon: "fa-solid fa-calendar-days",
            match: (p) => p === "/agenda", 
        },
      /*  {
            to: "/agenda/intervention",
            label: "Agenda Intervention",
            icon: "fa-solid fa-calendar-days",
            match: (p) => p.startsWith("/agenda/intervention"),
        },*/
    ] : [];



    const updateIndicator = useCallback(() => {
        const container = menuRef.current;
        const indicator = indicatorRef.current;
        if (!container || !indicator) return;

        const active = container.querySelector('[data-active="true"]');
        if (!active) {
            indicator.style.opacity = '0';
            return;
        }

        const cRect = container.getBoundingClientRect();
        const aRect = active.getBoundingClientRect();

        const top = aRect.top - cRect.top + container.scrollTop;

        indicator.style.opacity = '1';
        indicator.style.top = `${top}px`;
        indicator.style.height = `${aRect.height}px`;
    }, []);

    useEffect(() => {
        updateIndicator();
    }, [location.pathname, isCollapsed, screenWidth, navItemsTop.length, navItemsMid.length, navItemsBottom.length, navItemsAgenda.length, isRightSideBarOpen, rightSideBarTab, updateIndicator]);

    useEffect(() => {
        const onResize = () => {
            setScreenWidth(window.innerWidth);
            setIsCollapsed(window.innerWidth <= 640);
            // laisser un tick pour le reflow avant maj
            requestAnimationFrame(updateIndicator);
        };
        window.addEventListener('resize', onResize);
        // 1er passage
        requestAnimationFrame(updateIndicator);
        return () => window.removeEventListener('resize', onResize);
    }, [updateIndicator]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target) &&
                popupTriggerRef.current &&
                !popupTriggerRef.current.contains(event.target)
            ) {
                if (isProfilePopupOpen) {
                    setIsProfilePopupOpen(false);
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isProfilePopupOpen]);

    const getActiveIndex = () => {
        const paths = ["/dashboard", "/sites", "/declarations", "/dommages", "/recours", "/expertises", "/intervenants", "/statistiques", "/agenda"];
        return paths.findIndex(path => location.pathname.startsWith(path));
    };

    useEffect(() => {
        if (isLoggedIn) refresh?.();
    }, [location, isLoggedIn, refresh]);

    const handleResize = useCallback(() => {
        setScreenWidth(window.innerWidth);
        setIsCollapsed(window.innerWidth <= 640);
    }, []);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const getTranslateY = () => {
        const index = getActiveIndex();
        if (screenWidth <= 700) {
            return `${(index * 3.5) - (index > 4 ? (index > 6 ? 9.4 : 10) : 10.5)}rem`;
        } else if (screenWidth <= 1200) {
            return `${(index * 3.5) - (index > 4 ? (index > 6 ? 9.4 : 10) : 10.5)}rem`;
        } else {
            return `${(index * 3.5) - (index > 4 ? (index > 6 ? 9.4 : 10) : 10.5)}rem`;
        }
    };

    const toggleProfilePopup = () => setIsProfilePopupOpen(v => !v);

    const [version, setVersion] = useState("");
    useEffect(() => {
        fetch("/version.txt").then(r => r.text()).then(t => setVersion(t.trim()));
    }, []);

    const unreadCount = notifications.filter(n => !n.Consulte_Date && !n.lu && !n.read).length || unread || 0;

    const isMessagingActive = isRightSideBarOpen && rightSideBarTab === "mess";

    // console.log("notifications", notifications);

    return (
        <>
            <div class={`flex fixed flex-col z-10 items-center transition-all duration-300 ease-in-out ${isCollapsed ? 'min-w-16' : 'w-[13%] md:min-w-[180px] min-w-[160px] rounded-r-lg'} h-full overflow-hidden text-gray-400 bg-gray-900`}>
                <a class={`flex items-center ${isCollapsed ? 'w-14' : 'w-full'} px-3 mt-3`} href="#">
                    <img src={logoSAV} class="h-8 object-center object-contain" alt="" onClick={() => setIsCollapsed(!isCollapsed)} />
                </a>
                <div class="px-2 w-full">
                    <div ref={menuRef} className="relative flex flex-col items-center mt-3 border-gray-700 border-t w-full">

                        {/* Indicateur orange auto */}
                        <div
                            ref={indicatorRef}
                            className="left-0 z-0 absolute bg-orange-600 rounded-lg w-full transition-all duration-300 ease-in-out"
                            style={{ top: 0, height: 0, opacity: 0 }}
                            aria-hidden
                        />

                        {/* Groupe haut */}
                        {navItemsTop.map((item) => {
                            const isActive = !isMessagingActive && item.match(location.pathname);
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    data-active={isActive ? "true" : "false"}
                                    className={`flex items-center h-12 px-3 mt-2 rounded z-[1] ${isCollapsed ? '' : 'w-full'} ${isActive ? 'text-indigo-100' : 'hover:text-gray-100 transition-colors'}`}
                                    onClick={() => setTimeout(updateIndicator, 0)}
                                >
                                    <div className="w-6 h-6 text-center">
                                        <i className={item.icon} />
                                    </div>
                                    <span className={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>
                                        {item.label}
                                    </span>
                                </Link>
                            );
                        })}

                        {/* Groupe milieu (si non-visiteur) */}
                        {navItemsMid.length > 0 && (
                            <div className="relative flex flex-col items-center mt-2 border-gray-700 border-t w-full">
                                {navItemsMid.map((item) => {
                                    const isActive = !isMessagingActive && item.match(location.pathname);
                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            data-active={isActive ? "true" : "false"}
                                            className={`flex items-center h-12 px-3 mt-2 rounded z-[1] ${isCollapsed ? '' : 'w-full'} ${isActive ? 'text-indigo-100' : 'hover:text-gray-100 transition-colors'}`}
                                            onClick={() => setTimeout(updateIndicator, 0)}
                                        >
                                            <div className="w-6 h-6 text-center">
                                                <i className={item.icon} />
                                            </div>
                                            <span className={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>{item.label}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                        {/* Groupe Agenda */}
                        {navItemsAgenda.length > 0 && (
                            <div className="relative flex flex-col items-center mt-2 border-gray-700 border-t w-full">
                                {navItemsAgenda.map((item) => {
                                    const isActive = !isMessagingActive && item.match(location.pathname);

                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            data-active={isActive ? "true" : "false"}
                                            className={`flex items-center h-12 px-3 mt-2 rounded z-[1] ${isCollapsed ? "" : "w-full"
                                                } ${isActive
                                                    ? "text-indigo-100"
                                                    : "hover:text-gray-100 transition-colors"
                                                }`}
                                            onClick={() => setTimeout(updateIndicator, 0)}
                                        >
                                            <div className="w-6 h-6 text-center">
                                                <i className={item.icon} />
                                            </div>
                                            <span
                                                className={`ml-3 text-sm font-medium ${isCollapsed ? "hidden" : ""
                                                    }`}
                                            >
                                                {item.label}
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                        {/* Groupe bas (si non-visiteur) */}
                        {navItemsBottom.length > 0 && (
                            <div className="relative flex flex-col items-center mt-2 border-gray-700 border-t w-full">
                                {navItemsBottom.map((item) => {
                                    const isActive =
                                        item.label === "Messagerie"
                                            ? isMessagingActive
                                            : (!isMessagingActive && item.match(location.pathname));

                                    const commonClass = `flex items-center h-12 px-3 mt-2 rounded z-[1] ${isCollapsed ? "" : "w-full"
                                        } ${isActive ? "text-indigo-100" : "hover:text-gray-100 transition-colors"}`;
                                    if (item.action) {
                                        return (
                                            <button
                                                key={item.label}
                                                type="button"
                                                data-active={isActive ? "true" : "false"}
                                                className={commonClass}
                                                onClick={() => {
                                                    item.action();
                                                    setTimeout(updateIndicator, 0);
                                                }}
                                            >
                                                <div className="w-6 h-6 text-center">
                                                    <i className={item.icon} />
                                                </div>
                                                <span className={`ml-3 text-sm font-medium ${isCollapsed ? "hidden" : ""}`}>
                                                    {item.label}
                                                </span>
                                            </button>
                                        );
                                    }
                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            data-active={isActive ? "true" : "false"}
                                            className={commonClass}
                                            onClick={() => setTimeout(updateIndicator, 0)}
                                        >
                                            <div className="w-6 h-6 text-center">
                                                <i className={item.icon} />
                                            </div>
                                            <span className={`ml-3 text-sm font-medium ${isCollapsed ? "hidden" : ""}`}>
                                                {item.label}
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}


                    </div>
                    {/* <div class="relative flex flex-col items-center mt-3 border-gray-700 border-t w-full">
                        <div
                            className={`bottom-0 left-0 z-0 absolute bg-orange-600 rounded-lg w-full h-12 ${getActiveIndex() == -1 ? 'hidden' : ''}
                                transition-all duration-300 ease-in-out *:cursor-pointer active-indicator`}
                            style={{
                                transform: `translateY(${getTranslateY()})`
                            }}
                        ></div>

                        <Link
                            to="/dashboard"
                            className={`flex z-[1] items-center ${isCollapsed ? "" : "w-full"} h-12 px-3 mt-2 rounded ${location.pathname === "/dashboard" ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                            onClick={() => getActiveIndex()}
                        >
                            <div class="w-6 h-6 text-center">
                                <i class="fa-solid fa-house-chimney"></i>
                            </div>
                            <span className={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Tableau de Bord</span>
                        </Link>

                        {!hasRole('visiteur') && (
                            <Link
                                to="/sites"
                                className={`flex z-[1] items-center ${isCollapsed ? "" : "w-full"} h-12 px-3 mt-2 rounded ${location.pathname.startsWith("/sites") ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                                onClick={() => getActiveIndex()}
                            >

                                <div class="w-6 h-6 text-center">
                                    <i class="fa-solid fa-city"></i>
                                </div>
                                <span className={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Sites</span>
                            </Link>
                        )}

                        <Link to="/declarations"
                            class={`flex z-[1] items-center ${isCollapsed ? '' : 'w-full'} h-12 px-3 mt-2 rounded ${location.pathname.startsWith("/declarations") ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                            onClick={() => getActiveIndex()}
                        >
                            <div class="w-6 h-6 text-center">
                                <i class="fa-solid fa-scroll"></i>
                            </div>
                            <span class={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Déclarations</span>
                        </Link>
                        {!hasRole('visiteur') && (
                            <Link
                                to="/dommages"
                                class={`flex z-[1] items-center ${isCollapsed ? '' : 'w-full'} h-12 px-3 mt-2 rounded ${location.pathname.startsWith("/dommages") ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                                onClick={() => getActiveIndex()}
                            >
                                <div class="w-6 h-6 text-center">
                                    <i class="fa-solid fa-fire-extinguisher"></i>
                                </div>
                                <span class={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Dommages</span>
                            </Link>
                        )}
                    </div>
                    {!hasRole('visiteur') && (
                        <>
                            <div class="relative flex flex-col items-center mt-2 border-gray-700 border-t w-full">
                                <Link
                                    to="#"
                                    class={`flex z-[1] items-center ${isCollapsed ? '' : 'w-full'} h-12 px-3 mt-2 rounded ${location.pathname === "" ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                                    onClick={() => getActiveIndex()}
                                >
                                    <div class="w-6 h-6 text-center">
                                        <i class="fa-solid fa-circle-notch"></i>
                                    </div>
                                    <span class={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Recours</span>
                                </Link>
                                <Link
                                    to="#"
                                    class={`flex z-[1] items-center ${isCollapsed ? '' : 'w-full'} h-12 px-3 mt-2 rounded ${location.pathname === "" ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                                    onClick={() => getActiveIndex()}
                                >
                                    <div class="w-6 h-6 text-center">
                                        <i class="fa-solid fa-comments"></i>
                                    </div>
                                    <span class={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Expertises</span>
                                </Link>
                                <Link
                                    to="/intervenants"
                                    class={`flex z-[1] items-center ${isCollapsed ? '' : 'w-full'} h-12 px-3 mt-2 rounded ${location.pathname.startsWith("/intervenants") ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                                    onClick={() => getActiveIndex()}
                                >
                                    <div class="w-6 h-6 text-center">
                                        <i class="fa-solid fa-screwdriver-wrench"></i>
                                    </div>
                                    <span class={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Intervenants</span>
                                </Link>
                            </div>
                            <div class="relative flex flex-col items-center mt-2 border-gray-700 border-t w-full">
                                <Link
                                    to="/statistiques"
                                    class={`flex z-[1] items-center ${isCollapsed ? '' : 'w-full'} h-12 px-3 mt-2 rounded ${location.pathname.startsWith("/statistiques") ? 'text-gray-400 text-indigo-100' : 'transition-all duration-300 hover:text-gray-100'}`}
                                    onClick={() => getActiveIndex()}
                                >
                                    <div class="w-6 h-6 text-center">
                                        <i class="fa-solid fa-chart-pie"></i>
                                    </div>
                                    <span class={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>Statistiques</span>
                                </Link>
                            </div>
                        </>
                    )} */}
                </div>
                <div className="flex flex-col mt-auto w-full">
                    <div className={`flex justify-between items-center px-4 py-3 border-gray-700 border-t w-full ${isCollapsed ? '*:hidden' : ''}`}>
                        <div className={`-ml-1 mr-1 w-8 h-8 rounded-full bg-transparent border border-black shadow-inner
                        transition-all duration-700 ease-in-out`} style={{ boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.83)' }}>
                        </div>
                        <div className={`-ml-1 mr-1 w-8 h-8 rounded-full bg-transparent border border-black shadow-inner
                        transition-all duration-700 ease-in-out`} style={{ boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.83)' }}>
                        </div>

                        <IASideBar />
                        <p className="font-bold text-gray-500 text-xs sm:text-sm" style={{ fontFamily: "'Source Code Pro', monospace" }}>v{version}</p>
                    </div>
                    <a ref={popupTriggerRef} class={`${isProfilePopupOpen ? 'active' : ''} hover-shine flex z-[1] mx-3 mb-3 rounded-xl justify-center items-center bg-gradient-to-tr from-orange-500 to-orange-600 
                        h-14 text-white cursor-pointer transition-all duration-300`} onClick={toggleProfilePopup}>
                        <div id="div_user" class={`relative flex items-center`}>
                            <svg class="stroke-current w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="-top-0.5 -left-1.5 absolute flex size-3.5">
                                    <span className="inline-flex absolute bg-orange-400 opacity-75 rounded-full w-full h-full animate-ping"></span>
                                    <span className="absolute flex justify-center items-center bg-orange-500 rounded-full w-3.5 h-3.5 font-bold text-[11px] text-white">
                                        {unreadCount}
                                    </span>
                                </span>
                            )}
                            <span class={`ml-2 text-white text-sm font-medium ${isCollapsed ? 'hidden' : ''}`}>{user ? `${user.Nom.charAt(0).toUpperCase() + user.Nom.slice(1)}` : 'Utilisateur'}</span>
                        </div>
                    </a>
                </div>
                <div ref={popupRef} className={`${isProfilePopupOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"} 
                    transition-all z-10 transition-discrete duration-300 right-0 bottom-16 left-0 absolute bg-gray-800 shadow-lg m-3 p-3 rounded-xl z-15`}>
                    <div className="flex flex-col space-y-2 *:text-gray-100">
                        {/*<Link to="/profile" className="flex items-center hover:bg-gray-700 sm:px-3 py-2 rounded-lg font-medium hover:text-gray-100 text-sm transition-all duration-300">
                            <i class="fa-solid fa-user"></i>
                            <span className="invisible sm:visible ml-3">Profil</span>
                        </Link>*/}
                        <Link onClick={() => {
                            setIsRightSideBarOpen(true);
                            setRightSideBarTab('notif');
                        }}
                            className="group relative flex items-center hover:bg-gray-700 sm:px-3 py-2 rounded-lg font-medium hover:text-gray-100 text-sm transition-all duration-300">
                            <i class="group-hover:text-yellow-300 transition-all group-hover:animate-[wiggle_1s_ease-in-out_infinite] duration-300 fa-solid fa-bell"></i>
                            <span className="invisible sm:visible ml-3">Notifications</span>
                            {notifications.filter(notif => !notif.Consulte_Date).length > 0 && (<span class="top-1 -left-0 absolute flex size-3.5">
                                <span class="inline-flex absolute bg-yellow-400 opacity-75 rounded-full w-full h-full animate-ping"></span>
                                <span class="absolute flex justify-center items-center bg-yellow-500 rounded-full w-3.5 h-3.5 font-bold text-[11px] text-white">
                                    {notifications.filter(notif => !notif.Consulte_Date).length}
                                </span>
                            </span>)}
                        </Link>
                        <Link className="group flex items-center hover:bg-gray-700 sm:px-3 py-2 rounded-lg font-medium hover:text-gray-100 text-sm transition-all duration-300">
                            <i class="group-hover:rotate-[20deg] transition-all duration-300 fa-gear fa-solid"></i>
                            <span className="invisible sm:visible ml-3">Paramètres</span>
                        </Link>
                        <button className={`flex items-center sm:px-3 py-2 bg-red-500/75 hover:bg-red-600/75 rounded-lg font-medium text-sm transition-all duration-300`} onClick={logout}>
                            <i class="fa-right-from-bracket fa-solid"></i>
                            <span className="invisible sm:visible ml-3">Déconnexion</span>
                        </button>
                    </div>
                </div>
            </div>
            <div class={`flex flex-col transition-all duration-300 ease-in-out ${isCollapsed ? 'min-w-16' : 'w-[13%] md:min-w-[180px] min-w-[160px] rounded-r-lg'} h-full overflow-hidden text-gray-300`}>
            </div>

            <RightSidebar
                isOpen={isRightSideBarOpen}
                setIsOpen={setIsRightSideBarOpen}
                defaultTab={rightSideBarTab}

                notifications={notifications}
                refreshUserNotifications={refresh}
            />

        </>
    );
}

export default SideBar;
